// Types shared between the Worker API and the React client.

export type AgentScope = 'stops' | 'routes' | 'drivers' | 'pod' | 'invoices';
export const AGENT_SCOPES: AgentScope[] = ['stops', 'routes', 'drivers', 'pod', 'invoices'];

/** Resources the query_locate2u tool can fetch. `summary` needs no scope. */
export type L2uResource = 'summary' | AgentScope;

/**
 * Extensions read straight off the bytes as UTF-8.
 *
 * Not exhaustive on its own — any `text/*` MIME type is decoded too.
 */
export const DECODED_EXTENSIONS = [
  'txt',
  'md',
  'markdown',
  'csv',
  'tsv',
  'json',
  'yaml',
  'yml',
  'log',
  'html',
  'htm',
  'xml',
] as const;

/**
 * Extensions whose text is recovered by Workers AI `toMarkdown` rather than by
 * decoding. The binding supports more than this (Office, images); the list is
 * what this deployment has actually exercised.
 */
export const CONVERTED_EXTENSIONS = ['pdf'] as const;

/**
 * Largest file conversion is attempted on at all — the same ceiling as an
 * upload, so any PDF the app accepts is one it will try to read.
 */
export const MAX_CONVERT_BYTES = 20 * 1024 * 1024;

/**
 * Above this, conversion streams to the Workers AI REST endpoint instead of
 * handing a blob to the `AI` binding.
 *
 * The binding needs the whole file materialised inside a 128 MB isolate, on top
 * of the copy the parsed upload already holds. A 19.95 MB PDF exhausted it in
 * production twice — the second time after the duplicate copy was removed, so
 * one copy is still too much. That single measurement is the only evidence
 * behind this number, hence a round one well below it rather than beside it.
 *
 * Under it, the binding is used: it is proven, needs no API token, and its
 * calls show up in the AI Gateway alongside chat and embeddings.
 */
export const MAX_BINDING_CONVERT_BYTES = 10 * 1024 * 1024;

/**
 * Every extension the Worker can index, however it gets the text.
 *
 * Shared so the upload hint in the Knowledge Library is derived from the same
 * list the Worker matches against. They used to be written out separately, and
 * drifted: the UI advertised PDF while the Worker had no way to read one, so
 * every PDF upload was stored and then marked `failed`. Keeping the hint
 * derived is what makes adding PDF here a single edit rather than two.
 */
export const INDEXABLE_EXTENSIONS = [
  ...DECODED_EXTENSIONS,
  ...CONVERTED_EXTENSIONS,
] as const;

export type SourceKind = 'doc' | 'text' | 'skill';
export type SourceStatus = 'pending' | 'processing' | 'ready' | 'failed';
export type Provider = 'anthropic' | 'openai' | 'google';

export interface Agent {
  id: string;
  name: string;
  purpose: string;
  colour: string;
  rules: string[];
  suggestions: string[];
  useKnowledge: boolean;
  useApi: boolean;
  scopes: AgentScope[];
  /** null means inherit the workspace default model. */
  model: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeSource {
  id: string;
  name: string;
  kind: SourceKind;
  /** Empty means available to every agent. */
  agentIds: string[];
  byteSize: number;
  mimeType: string | null;
  status: SourceStatus;
  error: string | null;
  hasFile: boolean;
  createdAt: string;
}

export interface WorkspaceUser {
  id: string;
  email: string;
  displayName: string | null;
  role: 'member' | 'admin';
  /** null until the invite is accepted and they sign in for the first time. */
  lastSignInAt: string | null;
  /** Set only for accounts created by an invite, not by hand in Supabase. */
  invitedAt: string | null;
  createdAt: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  queriedResources: L2uResource[];
  createdAt: string;
}

export interface ModelOption {
  model: string;
  provider: Provider;
  displayName: string;
  inputPerMTok: number;
  outputPerMTok: number;
}

export interface WorkspaceSettings {
  defaultModel: string;
  /** 0 means unlimited. */
  monthlyTokenLimit: number;
  alertThresholdPct: number;
  l2uConnected: boolean;
  l2uEnvironment: 'Production' | 'Sandbox';
  l2uKeyLastFour: string | null;
  l2uScopes: Record<AgentScope, boolean>;
}

export interface UsageTotals {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  requestCount: number;
}

export interface UsageRequestRow {
  id: string;
  agentName: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  status: 'ok' | 'error' | 'blocked';
  createdAt: string;
}

export interface BudgetState {
  used: number;
  limit: number;
  remaining: number;
  pct: number;
  /** ok = under the alert threshold, warning = past it, blocked = at the cap. */
  status: 'ok' | 'warning' | 'blocked';
  spendUsd: number;
}

export interface ApiError {
  error: string;
  detail?: string;
}
